<script lang="ts">
  import {onMount} from 'svelte';
  import {deal, dealId} from 'Store/InvestorFlowStore';
  import {centsToString} from 'Utility/formatters/currency';
  import InvitationContent from 'Components/InvestorFlow/v2/Components/ProductPageDetails/InvitationContent.svelte';
  import Banner from 'Components/IncentivePlan/Banner.svelte';
  import {incentivePlans} from 'Store/deals/incentivePlan';
  import {
    groupedIncentiveTiers,
    groupIncentiveTiers,
  } from 'Store/incentiveTierStore';
  import IncentiveTierCards from 'Components/InvestorFlow/v2/Components/ProductPageDetails/IncentiveTierCards.svelte';
  import {isFreeSharesTier} from 'Store/deals/incentiveTiers/tierTypesUtils';

  $: hideUnitConverter = $deal?.feature_flags?.hide_flow_unit_converter;

  $: pricePerSecurityFormat = centsToString($deal.price_per_security, {
    removeZeroCents: false,
  });

  onMount(async () => {
    await incentivePlans.load($dealId);
    await groupIncentiveTiers($dealId, $deal.feature_flags, true);
  });

  $: showDealIncentiveTier =
    $deal.free_shares_deal ||
    $deal.time_deal ||
    $deal.loyalty_deal ||
    $deal.volume_deal;
  $: incentiveTierPresent = $groupedIncentiveTiers.length > 0;
  $: showPriceShare = !$groupedIncentiveTiers.some(([tierType]) =>
    isFreeSharesTier(tierType)
  );
</script>

<div class="product-page-details">
  <div class="details-title">
    <strong>Investment Details</strong>
  </div>

  {#if !$deal.feature_flags?.enable_uk_only_reg_s}
    <span class="common-subtitle" data-testid="raise-type">
      {$deal.raise_type}
    </span>
  {/if}

  {#if !hideUnitConverter}
    <div class="price-per-security" data-testid="currency">
      {#if $incentivePlans?.effective_tier}
        <Banner dealId={$dealId} dealStore={deal} />
      {:else if showDealIncentiveTier}
        {#if showPriceShare}
          <div class="price-per-security-text" data-testid="price-per-security">
            <span
              >Price Per {$deal.singularize_security_type}:
              <strong>{$deal.currency_symbol}{pricePerSecurityFormat}</strong
              ></span
            >
          </div>
        {/if}
        <div class="my-3">
          {#if incentiveTierPresent}
            <IncentiveTierCards
              groupedIncentiveTiers={$groupedIncentiveTiers}
            />
          {/if}
        </div>
      {:else}
        {$deal.currency_symbol}{pricePerSecurityFormat}
        {$deal.currency}
        <span class="common-subtitle"
          >Per {$deal.singularize_security_type}</span
        >
      {/if}
    </div>
  {:else}
    <div class="security-offering" data-testid="security-offering">
      {$deal.singularize_security_type} Offering
    </div>
  {/if}

  {#if $deal.page_content?.body}
    <InvitationContent />
  {/if}
</div>

<style lang="postcss">
  .product-page-details {
    & div:not(:last-child) {
      border-bottom: 1px solid var(--palettes-neutral-75, #eaecf0);
    }
  }

  .details-title {
    font-size: 1.2rem;
    line-height: 1.25rem;
    padding: 0.75rem 0;
    color: var(--palettes-neutral-575, #667085);
    margin-bottom: 0.75rem;
  }

  .price-per-security {
    display: flex;
    flex-direction: column;
    font-size: 1rem;
    line-height: 1.25rem;
    color: var(--palettes-neutral-575, #667085);
    padding-bottom: 0.75rem;

    @media screen and (min-width: 768px) {
      font-weight: 500;
      font-size: 1.25rem;
      line-height: 2rem;
    }
  }

  .security-offering {
    font-size: 1rem;
    line-height: 1.25rem;
    color: var(--palettes-neutral-575, #667085);
    padding: 0.75rem 0;
  }

  .common-subtitle {
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.25rem;
    color: var(--palettes-neutral-575, #667085);
    padding: 0.25rem 0;
  }

  .price-per-security-text {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--palettes-neutral-650, #667085);
    margin-bottom: 0.5rem;
    padding-bottom: 0.75rem;
    font-weight: 400;

    & strong {
      font-weight: 600;
    }
  }
</style>
